import React, { useState, useEffect, useCallback } from "react";
import AttackersSelection from "./attackersSelection";
import DefendersSelection from "./defendersSelection";
import SoldierUnitAsRender from "./soldierUnitAsRender";
import { loadAllConfigs } from "../utils/configLoader";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Box from "@mui/material/Box";
import { analytics, isLocal } from "./../firebase";
import { logEvent } from "firebase/analytics";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CardWithShadow from "./cardWithShadow";
import {
    MAX_WIDTH_PX,
    SINGLE_COL_MAX_WIDTH_PX,
    SINGLE_COLUMN_WIDTH_PERCENTAGE,
} from "../customStyles";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import { SoldierUnit } from "../types/SoldierUnit";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import { pink } from "@mui/material/colors";
import { UnitConfig, VersionConfig } from "../types/VersionConfig";
import { useSearchParams } from "react-router-dom";
import { LATEST_VERSION } from "../config/version.global";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import IconButton from '@mui/material/IconButton';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragStartEvent,
    DragEndEvent,
    MeasuringStrategy,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSwappingStrategy,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const analyticsLogEvent = isLocal ? analytics.logEvent : logEvent;

interface SortableCardProps {
    soldierUnit: SoldierUnit;
    index: number;
    team: string;
    children: React.ReactNode;
    onDelete: (id: number, team: string) => void;
}

interface DraggableCardProps {
    dragHandleProps?: {
        [key: string]: any;
    };
}

const SortableCard = ({ soldierUnit, index, team, children, onDelete }: SortableCardProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ 
        id: `${team}-${soldierUnit.id}`,
        data: {
            team,
            id: soldierUnit.id
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? transition : 'none', // Only apply transition during dragging
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
        zIndex: isDragging ? 1000 : 1,
        gridColumn: 'span 1',
        display: 'block',
        width: '100%',
    };

    return (
        <div ref={setNodeRef} style={style}>
            {children}
            <div 
                {...attributes}
                {...listeners}
                style={{
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    right: 2,
                    zIndex: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: team === 'Attackers' ? '#1976d2' : '#d32f2f',
                    borderRadius: '4px',
                    padding: '2px',
                    pointerEvents: 'auto',
                    cursor: 'grab',
                    userSelect: 'none',
                    touchAction: 'none',
                }}
            >
                <div 
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '1.2rem',
                        pointerEvents: 'none',
                    }}
                >
                    <DragIndicatorIcon style={{ fontSize: '1.2rem', pointerEvents: 'none' }} />
                    <span style={{ pointerEvents: 'none' }}>#{index + 1}</span>
                </div>
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent drag when clicking delete button
                        onDelete(soldierUnit.id, team);
                    }}
                    style={{
                        padding: '4px',
                        backgroundColor: 'transparent',
                        pointerEvents: 'auto', // Make sure delete button is clickable
                    }}
                    disableRipple
                    sx={{ "&:hover": { backgroundColor: team === 'Attackers' ? '#1565c0' : '#c62828' } }}
                >
                    <CancelPresentationIcon
                        sx={{ color: 'white', fontSize: '24px' }}
                    />
                </IconButton>
            </div>
        </div>
    );
};

const BattleGroundDetails = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [versionConfigs, setVersionConfigs] = useState<
        Record<string, VersionConfig>
    >({});
    const [versionConfig, setVersionConfig] = useState<VersionConfig>();
    const [isLoading, setIsLoading] = useState(true);

    const [soldierUnitsAttackersAsRender, setSoldierUnitsAttackersAsRender] =
        useState<SoldierUnit[]>([]);
    const [soldierUnitsDefendersAsRender, setSoldierUnitsDefendersAsRender] =
        useState<SoldierUnit[]>([]);
    // const [defIdxArray, setDefIdxArray] = useState<number[]>([]);
    // const [attIdxArray, setAttIdxArray] = useState<number[]>([]);
    const [checkedPosition, setCheckedPosition] = useState(false);

    useEffect(() => {
        loadAllConfigs().then((configs) => {
            setVersionConfigs(configs);
            setVersionConfig(
                configs[searchParams.get("version") ?? LATEST_VERSION]
            );
            setIsLoading(false);
        });
    }, []);

    const getUnitConfig = useCallback(
        (typeUnit: string): UnitConfig => {
            if (!versionConfig) {
                throw new Error("Version config not loaded yet.");
            }

            const unit = versionConfig.unitStats.find(
                (unit) => unit.name === typeUnit
            );

            if (!unit) {
                throw new Error("Unit type not found.");
            }

            return unit;
        },
        [versionConfig]
    );

    const handleGameVersionChange = (event: SelectChangeEvent) => {
        setSoldierUnitsAttackersAsRender([]);
        setSoldierUnitsDefendersAsRender([]);

        const version = event.target.value;
        analyticsLogEvent(analytics, "pc_version_changed_" + version);
        setSearchParams({ version: version });
        setVersionConfig(versionConfigs[version]);
    };

    const handleChangeCheckbox = (): void => {
        setCheckedPosition((prev) => !prev);
        analyticsLogEvent(analytics, "pc_checkbox_toggled");
    };

    const handleAddAttacker = (typeUnit: string): void => {
        const newId = soldierUnitsAttackersAsRender.length;

        try {
            const unitConfig = getUnitConfig(typeUnit);

            const newUnit: SoldierUnit = {
                id: newId,
                config: unitConfig,
                typeUnit,
                team: "Attackers",
                healthMax: unitConfig.maxHealth,
                healthBefore: unitConfig.maxHealth,
                healthAfter: unitConfig.maxHealth,
                veteran: false,
                defenceBonus: false,
                wallBonus: false,
                safeBonus: unitConfig.skills.includes("surprise"),
                poisonedBonus: false,
                becamePoisonedBonus: false,
                boostedBonus: false,
                shipUnit: unitConfig.skills.includes("variableHp"),
                splashDamage: false,
                explodeDamage: false,
            };
            setSoldierUnitsAttackersAsRender((prev) => [...prev, newUnit]);
            // setAttIdxArray((prev) => [...prev, newId]);
            analyticsLogEvent(analytics, "pc_attacker_added_" + typeUnit);
        } catch (e: any) {
            alert("Error: failed to add attacker.");
        }
    };

    const handleAddDefender = (typeUnit: string): void => {
        const newId = soldierUnitsDefendersAsRender.length;

        try {
            const unitConfig = getUnitConfig(typeUnit);

            const newUnit: SoldierUnit = {
                id: newId,
                config: unitConfig,
                typeUnit,
                team: "Defenders",
                healthMax: unitConfig.maxHealth,
                healthBefore: unitConfig.maxHealth,
                healthAfter: unitConfig.maxHealth,
                veteran: false,
                defenceBonus: false,
                wallBonus: false,
                safeBonus: false,
                poisonedBonus: false,
                becamePoisonedBonus: false,
                boostedBonus: false,
                shipUnit: unitConfig.skills.includes("variableHp"),
                splashDamage: false,
                explodeDamage: false,
            };
            setSoldierUnitsDefendersAsRender((prev) => [...prev, newUnit]);
            // setDefIdxArray((prev) => [...prev, newId]);
            analyticsLogEvent(analytics, "pc_defender_added_" + typeUnit);
        } catch (e) {
            alert("Error: failed to add defender.");
        }
    };

    const handleUpdateHitpoints = (
        id: number,
        team: string,
        healthBeforeManualInput: number
    ) => {
        if (team === "Attackers") {
            setSoldierUnitsAttackersAsRender((prev) =>
                prev.map((u) =>
                    u.id === id
                        ? { ...u, healthBefore: healthBeforeManualInput }
                        : u
                )
            );
        } else {
            setSoldierUnitsDefendersAsRender((prev) =>
                prev.map((u) =>
                    u.id === id
                        ? { ...u, healthBefore: healthBeforeManualInput }
                        : u
                )
            );
        }
        analyticsLogEvent(analytics, "pc_hitpoints_direct_" + team);
    };

    const handleIncreaseHitpoints = (id: number, team: string) => {
        if (!checkedPosition) {
            if (team === "Attackers") {
                setSoldierUnitsAttackersAsRender((prev) =>
                    prev.map((u) =>
                        u.id === id
                            ? { ...u, healthBefore: u.healthBefore + 1 }
                            : u
                    )
                );
            } else {
                setSoldierUnitsDefendersAsRender((prev) =>
                    prev.map((u) =>
                        u.id === id
                            ? { ...u, healthBefore: u.healthBefore + 1 }
                            : u
                    )
                );
            }
            analyticsLogEvent(analytics, "pc_hitpoints_plus_" + team);
        } else {
            // Move item up in array
            if (team === "Attackers") {
                setSoldierUnitsAttackersAsRender((prev) => {
                    const idx = prev.findIndex((u) => u.id === id);
                    if (idx <= 0) return prev;
                    const arr = [...prev];
                    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                    return arr;
                });
            } else {
                setSoldierUnitsDefendersAsRender((prev) => {
                    const idx = prev.findIndex((u) => u.id === id);
                    if (idx <= 0) return prev;
                    const arr = [...prev];
                    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                    return arr;
                });
            }
            analyticsLogEvent(analytics, "pc_changed_position_up_" + team);
        }
    };

    const handleDecreaseHitpoints = (id: number, team: string) => {
        if (!checkedPosition) {
            if (team === "Attackers") {
                setSoldierUnitsAttackersAsRender((prev) =>
                    prev.map((u) =>
                        u.id === id
                            ? { ...u, healthBefore: u.healthBefore - 1 }
                            : u
                    )
                );
            } else {
                setSoldierUnitsDefendersAsRender((prev) =>
                    prev.map((u) =>
                        u.id === id
                            ? { ...u, healthBefore: u.healthBefore - 1 }
                            : u
                    )
                );
            }
            analyticsLogEvent(analytics, "pc_hitpoints_min_" + team);
        } else {
            // Move item down in array
            if (team === "Attackers") {
                setSoldierUnitsAttackersAsRender((prev) => {
                    const idx = prev.findIndex((u) => u.id === id);
                    if (idx === prev.length - 1 || idx < 0) return prev;
                    const arr = [...prev];
                    [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]];
                    return arr;
                });
            } else {
                setSoldierUnitsDefendersAsRender((prev) => {
                    const idx = prev.findIndex((u) => u.id === id);
                    if (idx === prev.length - 1 || idx < 0) return prev;
                    const arr = [...prev];
                    [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]];
                    return arr;
                });
            }
            analyticsLogEvent(analytics, "pc_changed_position_down_" + team);
        }
    };

    const handleDelete = (id: number, team: string) => {
        if (team === "Attackers") {
            setSoldierUnitsAttackersAsRender((prev) =>
                prev.filter((u) => u.id !== id).map((u, i) => ({ ...u, id: i }))
            );
            // setAttIdxArray((prev) =>
            //     prev.filter((x) => x !== id).map((_, i) => i)
            // );
        } else {
            setSoldierUnitsDefendersAsRender((prev) =>
                prev.filter((u) => u.id !== id).map((u, i) => ({ ...u, id: i }))
            );
            // setDefIdxArray((prev) =>
            //     prev.filter((x) => x !== id).map((_, i) => i)
            // );
        }
        analyticsLogEvent(analytics, "pc_unit_deleted_" + team);
    };

    // For toggling states like defenceBonus, wallBonus, etc.
    const toggleBonus = (
        id: number,
        team: string,
        which:
            | "defenceBonus"
            | "wallBonus"
            | "poisonedBonus"
            | "safeBonus"
            | "boostedBonus"
            | "splashDamage"
            | "explodeDamage",
        typeUnit: string,
        code: string
    ) => {
        if (team === "Defenders") {
            setSoldierUnitsDefendersAsRender((prev) =>
                prev.map((u) => {
                    if (u.id === id) {
                        const updated = { ...u };
                        updated[which] = !u[which];
                        // Certain combos disable others
                        if (which === "defenceBonus" && updated[which]) {
                            updated.wallBonus = false;
                            updated.poisonedBonus = false;
                        }
                        if (which === "wallBonus" && updated[which]) {
                            updated.defenceBonus = false;
                            updated.poisonedBonus = false;
                        }
                        if (which === "poisonedBonus" && updated[which]) {
                            updated.wallBonus = false;
                            updated.defenceBonus = false;
                        }
                        return updated;
                    }
                    return u;
                })
            );
        } else {
            setSoldierUnitsAttackersAsRender((prev) =>
                prev.map((u) => {
                    if (u.id === id) {
                        return { ...u, [which]: !u[which] };
                    }
                    return u;
                })
            );
        }
        analyticsLogEvent(analytics, code + "_" + typeUnit);
    };

    const handleDefenceBonus = (
        id: number,
        team: string,
        typeUnit: string,
        value: boolean
    ) => toggleBonus(id, team, "defenceBonus", typeUnit, "pc_defence_bonus");

    const handleWallBonus = (
        id: number,
        team: string,
        typeUnit: string,
        value: boolean
    ) => toggleBonus(id, team, "wallBonus", typeUnit, "pc_wall_bonus");

    const handlePoisonedBonus = (
        id: number,
        team: string,
        typeUnit: string,
        value: boolean
    ) => toggleBonus(id, team, "poisonedBonus", typeUnit, "pc_poisoned_bonus");

    const handleSafeBonus = (
        id: number,
        team: string,
        typeUnit: string,
        value: boolean
    ) => toggleBonus(id, team, "safeBonus", typeUnit, "pc_safe_bonus");

    const handleBoostedBonus = (
        id: number,
        team: string,
        typeUnit: string,
        value: boolean
    ) => toggleBonus(id, team, "boostedBonus", typeUnit, "pc_boosted_bonus");

    const handleSplashDamage = (
        id: number,
        team: string,
        typeUnit: string,
        value: boolean
    ) =>
        toggleBonus(
            id,
            team,
            "splashDamage",
            typeUnit,
            "pc_splash_damage_toggled"
        );

    const handleExplodeDamage = (
        id: number,
        team: string,
        typeUnit: string,
        value: boolean
    ) =>
        toggleBonus(
            id,
            team,
            "explodeDamage",
            typeUnit,
            "pc_explode_damage_toggled"
        );

    const handleVeteranBonus = (id: number, team: string, typeUnit: string) => {
        if (team === "Attackers") {
            setSoldierUnitsAttackersAsRender((prev) =>
                prev.map((u) => {
                    if (u.id === id) {
                        const wasVet = u.veteran;
                        const newMax = wasVet
                            ? u.config.maxHealth
                            : u.config.maxHealth + 5;
                        return {
                            ...u,
                            veteran: !wasVet,
                            healthMax: newMax,
                            healthBefore: newMax,
                        };
                    }
                    return u;
                })
            );
        } else {
            setSoldierUnitsDefendersAsRender((prev) =>
                prev.map((u) => {
                    if (u.id === id) {
                        const wasVet = u.veteran;
                        const newMax = wasVet
                            ? u.config.maxHealth
                            : u.config.maxHealth + 5;
                        return {
                            ...u,
                            veteran: !wasVet,
                            healthMax: newMax,
                            healthBefore: newMax,
                        };
                    }
                    return u;
                })
            );
        }
        analyticsLogEvent(
            analytics,
            "pc_veterancy_bonus_" + team + "_" + typeUnit
        );
    };

    const handleShipUnit = (
        id: number,
        team: string,
        typeUnit: string,
        shipUnit: boolean
    ) => {
        // Not fully replicating the old logic but adjusting health
        if (team === "Attackers") {
            setSoldierUnitsAttackersAsRender((prev) =>
                prev.map((u) => {
                    if (u.id === id) {
                        const maxHealth =
                            u.healthMax + 5 > 35 ? 10 : u.healthMax + 5;
                        return {
                            ...u,
                            healthMax: maxHealth,
                            healthBefore: maxHealth,
                        };
                    }
                    return u;
                })
            );
        } else {
            setSoldierUnitsDefendersAsRender((prev) =>
                prev.map((u) => {
                    if (u.id === id) {
                        const maxHealth =
                            u.healthMax + 5 > 35 ? 10 : u.healthMax + 5;
                        return {
                            ...u,
                            healthMax: maxHealth,
                            healthBefore: maxHealth,
                        };
                    }
                    return u;
                })
            );
        }
        analyticsLogEvent(analytics, "pc_ship_mx_" + team + "_" + typeUnit);
    };

    const handleDuplicate = (id: number, team: string) => {
        if (team === "Attackers") {
            setSoldierUnitsAttackersAsRender((prev) => {
                const unitToDuplicate = prev.find(u => u.id === id);
                if (!unitToDuplicate) return prev;
                
                const newUnit = {
                    ...unitToDuplicate,
                    id: prev.length // New ID will be the length of the array
                };
                
                return [...prev, newUnit];
            });
            analyticsLogEvent(analytics, "pc_attacker_duplicated");
        } else {
            setSoldierUnitsDefendersAsRender((prev) => {
                const unitToDuplicate = prev.find(u => u.id === id);
                if (!unitToDuplicate) return prev;
                
                const newUnit = {
                    ...unitToDuplicate,
                    id: prev.length // New ID will be the length of the array
                };
                
                return [...prev, newUnit];
            });
            analyticsLogEvent(analytics, "pc_defender_duplicated");
        }
    };

    const healthAfterCalculation = useCallback(() => {
        console.log("Charge!");
        analyticsLogEvent(analytics, "pc_magic_happens");

        const attList = [...soldierUnitsAttackersAsRender];
        const defList = [...soldierUnitsDefendersAsRender];

        attList.forEach((a) => {
            a.healthAfter = a.healthBefore;
        });
        defList.forEach((d) => {
            d.healthAfter = d.healthBefore;
        });

        let idxDefPos = 0;
        let totalAttackResult = 0;
        let poisoningAttacker = 9999;
        let defenderRepeatedAttack = 0;

        attList.forEach((attacker) => {
            const defender = defList[idxDefPos];
            if (!defender) return;

            if (defenderRepeatedAttack === 0) {
                defender.becamePoisonedBonus = false;
            }
            defenderRepeatedAttack++;

            const safeBonusMultiplier = attacker.safeBonus ? 0 : 1;
            let defenceBonusMultiplier = defender.defenceBonus ? 1.5 : 1;
            let wallBonusMultiplier = defender.wallBonus ? 4 : 1;
            let poisonedBonusMultiplier = 1;

            if (defender.poisonedBonus) {
                poisonedBonusMultiplier = 0.7;
                wallBonusMultiplier = 1;
                defenceBonusMultiplier = 1;
            }
            if (attacker.id > poisoningAttacker) {
                poisonedBonusMultiplier = 0.7;
                wallBonusMultiplier = 1;
                defenceBonusMultiplier = 1;
            }

            const boostedBonusMultiplier = attacker.boostedBonus ? 1 : 0;

            const attackForce = parseFloat(
                (
                    ((attacker.config.attack + 0.5 * boostedBonusMultiplier) *
                        attacker.healthBefore) /
                    attacker.healthMax
                ).toFixed(10)
            );

            const defenceForce = parseFloat(
                (
                    ((defender.config.defence *
                        (defender.healthBefore - totalAttackResult)) /
                        defender.healthMax) *
                    wallBonusMultiplier *
                    defenceBonusMultiplier *
                    poisonedBonusMultiplier
                ).toFixed(10)
            );

            const totalDamage = attackForce + defenceForce;

            let attackResult = Math.round(
                parseFloat(
                    (
                        (attackForce / totalDamage) *
                        (attacker.config.attack +
                            0.5 * boostedBonusMultiplier) *
                        4.5
                    ).toFixed(10)
                )
            );

            if (
                attacker.splashDamage &&
                (attacker.config.skills.includes("splash") ||
                    attacker.config.skills.includes("stomp"))
            ) {
                attackResult = Math.round(attackResult * 0.5);
            }
            if (attacker.explodeDamage || attacker.typeUnit === "Segment") {
                attackResult = Math.round(attackResult * 0.5);
            }

            console.log("This is attackForce:" + attackForce);
            console.log("This is defenceForce:" + defenceForce);
            console.log("This is totalDamage:" + totalDamage);
            console.log("This is attackResult:" + attackResult);

            totalAttackResult += attackResult;
            defender.healthAfter = defender.healthBefore - totalAttackResult;

            if (defender.healthAfter > 0) {
                const defenceResult = Math.round(
                    parseFloat(
                        (
                            (defenceForce / totalDamage) *
                            defender.config.defence *
                            4.5
                        ).toFixed(10)
                    )
                );
                console.log("This is defenceResult:" + defenceResult);
                if (
                    ["Exida", "Phychi", "Kiton", "Segment"].includes(
                        attacker.typeUnit
                    ) ||
                    attacker.explodeDamage
                ) {
                    defender.becamePoisonedBonus = true;
                    poisoningAttacker = attacker.id;
                }
                attacker.healthAfter =
                    attacker.healthBefore - defenceResult * safeBonusMultiplier;
                if (attacker.typeUnit === "Segment" || attacker.explodeDamage) {
                    attacker.healthAfter = 0;
                }
            } else {
                idxDefPos++;
                totalAttackResult = 0;
                poisoningAttacker = 9999;
                defenderRepeatedAttack = 0;
            }
        });

        console.log("attList:");
        console.log(attList);
        console.log("defList:");
        console.log(defList);

        return [attList, defList];
    }, [
        soldierUnitsAttackersAsRender,
        soldierUnitsDefendersAsRender,
        analyticsLogEvent,
    ]);

    useEffect(() => {
        // Replicates componentDidUpdate-ish logging
        console.log("battleGroundDetails updated");
    });

    // Recomputes health after renders, similar to original approach
    healthAfterCalculation();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum drag distance before activation
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const [activeTeam, activeId] = active.id.toString().split('-');
        const activeIndex = parseInt(activeId);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over) return;

        const activeId = active.id as string;
        const overId = over.id as string;
        
        if (activeId === overId) return;

        const [activeTeam, activeIdNum] = activeId.split('-');
        const [overTeam, overIdNum] = overId.split('-');
        
        if (activeTeam !== overTeam) return; // Prevent cross-column dragging

        const activeIndex = parseInt(activeIdNum);
        const overIndex = parseInt(overIdNum);

        if (activeTeam === 'Attackers') {
            setSoldierUnitsAttackersAsRender((items) => {
                const oldIndex = items.findIndex(item => item.id === activeIndex);
                const newIndex = items.findIndex(item => item.id === overIndex);
                
                const newItems = [...items];
                const [movedItem] = newItems.splice(oldIndex, 1);
                newItems.splice(newIndex, 0, movedItem);
                
                // Update all IDs to match their new positions
                return newItems.map((item, index) => ({
                    ...item,
                    id: index
                }));
            });
            analyticsLogEvent(analytics, "pc_attacker_dragged");
        } else if (activeTeam === 'Defenders') {
            setSoldierUnitsDefendersAsRender((items) => {
                const oldIndex = items.findIndex(item => item.id === activeIndex);
                const newIndex = items.findIndex(item => item.id === overIndex);
                
                const newItems = [...items];
                const [movedItem] = newItems.splice(oldIndex, 1);
                newItems.splice(newIndex, 0, movedItem);
                
                // Update all IDs to match their new positions
                return newItems.map((item, index) => ({
                    ...item,
                    id: index
                }));
            });
            analyticsLogEvent(analytics, "pc_defender_dragged");
        }
    };

    return (
        <Box
            sx={{
                width: "95%",
                maxWidth: "2400px",
                minWidth: "1100px",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
            }}
        >
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                measuring={{
                    droppable: {
                        strategy: MeasuringStrategy.Always,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0,
                        gap: "10px",
                        minHeight: "400px",
                        position: "relative",
                    }}
                >
                    {/* Attackers Column */}
                    <Box
                        sx={{
                            width: "49.5%",
                            minWidth: "500px",
                        }}
                    >
                        {soldierUnitsAttackersAsRender.length === 0 && (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    mb: 2,
                                    p: 2,
                                    backgroundColor: "#1976d2",
                                    color: "white",
                                    borderRadius: "4px",
                                    fontWeight: "bold",
                                    fontSize: "1.2rem"
                                }}
                            >
                                Attackers Side
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: "10px",
                                alignContent: "start",
                                width: "100%",
                            }}
                        >
                            <SortableContext
                                items={soldierUnitsAttackersAsRender.map(u => `Attackers-${u.id}`)}
                                strategy={rectSwappingStrategy}
                            >
                                {soldierUnitsAttackersAsRender.map((soldierUnitAtt, index) => (
                                    <SortableCard
                                        key={`Attackers-${soldierUnitAtt.id}`}
                                        soldierUnit={soldierUnitAtt}
                                        index={index}
                                        team="Attackers"
                                        onDelete={handleDelete}
                                    >
                                        <CardWithShadow
                                            sx={{
                                                position: 'relative',
                                                padding: '40px 5px 5px', /* Increased top padding to make room for the handle */
                                                height: '180px',
                                                marginBottom: '10px',
                                                cursor: 'default',
                                                '& img': {
                                                    width: '50px !important',
                                                    height: '50px !important',
                                                    objectFit: 'contain',
                                                    margin: '0 auto',
                                                    display: 'block',
                                                    marginTop: '10px', /* Added margin to move image down */
                                                },
                                            }}
                                        >

                                            <SoldierUnitAsRender
                                                key={`attacker-unit-${soldierUnitAtt.id}-${soldierUnitAtt.typeUnit}`}
                                                soldierUnit={soldierUnitAtt}
                                                onDelete={handleDelete}
                                                onUpdateHitpoints={handleUpdateHitpoints}
                                                onIncreaseHitpoints={handleIncreaseHitpoints}
                                                onDecreaseHitpoints={handleDecreaseHitpoints}
                                                onVeteranBonus={handleVeteranBonus}
                                                onDefenceBonus={handleDefenceBonus}
                                                onWallBonus={handleWallBonus}
                                                onSafeBonus={handleSafeBonus}
                                                onPoisonedBonus={handlePoisonedBonus}
                                                onBoostedBonus={handleBoostedBonus}
                                                onShipUnit={handleShipUnit}
                                                onSplashDamage={handleSplashDamage}
                                                onExplodeDamage={handleExplodeDamage}
                                                onDuplicate={handleDuplicate}
                                                showDeleteInCard={false}
                                            />
                                        </CardWithShadow>
                                    </SortableCard>
                                ))}
                            </SortableContext>
                        </Box>
                    </Box>

                    {/* Switch Units Button */}
                    <Box
                        sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 10
                        }}
                    >
                        <IconButton
                            onClick={() => {
                                // Switch units between attackers and defenders
                                const newAttackers = soldierUnitsDefendersAsRender.map((unit, index) => ({
                                    ...unit,
                                    id: index,
                                    team: "Attackers"
                                }));
                                
                                const newDefenders = soldierUnitsAttackersAsRender.map((unit, index) => ({
                                    ...unit,
                                    id: index,
                                    team: "Defenders"
                                }));
                                
                                setSoldierUnitsAttackersAsRender(newAttackers);
                                setSoldierUnitsDefendersAsRender(newDefenders);
                                analyticsLogEvent(analytics, "pc_units_switched");
                            }}
                            sx={{
                                backgroundColor: "#f5f5f5",
                                border: "2px solid #1976d2",
                                "&:hover": {
                                    backgroundColor: "#e0e0e0",
                                }
                            }}
                            size="large"
                            aria-label="switch units"
                        >
                            <SwapHorizIcon fontSize="large" />
                        </IconButton>
                    </Box>

                    {/* Defenders Column */}
                    <Box
                        sx={{
                            width: "49.5%",
                            minWidth: "500px",
                        }}
                    >
                        {soldierUnitsDefendersAsRender.length === 0 && (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    mb: 2,
                                    p: 2,
                                    backgroundColor: "#d32f2f",
                                    color: "white",
                                    borderRadius: "4px",
                                    fontWeight: "bold",
                                    fontSize: "1.2rem"
                                }}
                            >
                                Defenders Side
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: "10px",
                                alignContent: "start",
                                width: "100%",
                            }}
                        >
                            <SortableContext
                                items={soldierUnitsDefendersAsRender.map(u => `Defenders-${u.id}`)}
                                strategy={rectSwappingStrategy}
                            >
                                {soldierUnitsDefendersAsRender.map((soldierUnitDef, index) => (
                                    <SortableCard
                                        key={`Defenders-${soldierUnitDef.id}`}
                                        soldierUnit={soldierUnitDef}
                                        index={index}
                                        team="Defenders"
                                        onDelete={handleDelete}
                                    >
                                        <CardWithShadow
                                            sx={{
                                                position: 'relative',
                                                padding: '40px 5px 5px', /* Increased top padding to make room for the handle */
                                                height: '180px',
                                                marginBottom: '10px',
                                                cursor: 'default',
                                                '& img': {
                                                    width: '50px !important',
                                                    height: '50px !important',
                                                    objectFit: 'contain',
                                                    margin: '0 auto',
                                                    display: 'block',
                                                    marginTop: '10px', /* Added margin to move image down */
                                                },
                                            }}
                                        >

                                            <SoldierUnitAsRender
                                                key={`defender-unit-${soldierUnitDef.id}-${soldierUnitDef.typeUnit}`}
                                                soldierUnit={soldierUnitDef}
                                                onDelete={handleDelete}
                                                onUpdateHitpoints={handleUpdateHitpoints}
                                                onIncreaseHitpoints={handleIncreaseHitpoints}
                                                onDecreaseHitpoints={handleDecreaseHitpoints}
                                                onVeteranBonus={handleVeteranBonus}
                                                onDefenceBonus={handleDefenceBonus}
                                                onWallBonus={handleWallBonus}
                                                onSafeBonus={handleSafeBonus}
                                                onPoisonedBonus={handlePoisonedBonus}
                                                onBoostedBonus={handleBoostedBonus}
                                                onShipUnit={handleShipUnit}
                                                onSplashDamage={handleSplashDamage}
                                                onExplodeDamage={handleExplodeDamage}
                                                onDuplicate={handleDuplicate}
                                                showDeleteInCard={false}
                                            />
                                        </CardWithShadow>
                                    </SortableCard>
                                ))}
                            </SortableContext>
                        </Box>
                    </Box>
                </Box>
            </DndContext>

            <CardWithShadow sx={{ p: "0 2%", width: "100%", mt: "2px" }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={checkedPosition}
                            onChange={handleChangeCheckbox}
                        />
                    }
                    label="Use + and - to set order instead of health"
                />
            </CardWithShadow>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                    mb: 0,
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        width: "49.5%",
                        minWidth: "500px",
                        '& > *': {
                            width: "100% !important",
                            minWidth: "100% !important",
                            '& > *': {
                                width: "100% !important",
                            }
                        }
                    }}
                >
                    <AttackersSelection
                        onAddAttacker={handleAddAttacker}
                        pageIndex={0}
                        disabled={isLoading}
                    />
                </Box>
                <Box
                    sx={{
                        width: "49.5%",
                        minWidth: "500px",
                        '& > *': {
                            width: "100% !important",
                            minWidth: "100% !important",
                            '& > *': {
                                width: "100% !important",
                            }
                        }
                    }}
                >
                    <DefendersSelection
                        onAddDefender={handleAddDefender}
                        pageIndex={0}
                        disabled={isLoading}
                    />
                </Box>
            </Box>

            {versionConfig && (
                <CardWithShadow sx={{ p: "3px 2%", width: "100%" }}>
                    <Box
                        component="span"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            typography: "body2",
                        }}
                    >
                        <FormControl sx={{ my: 1, minWidth: 120 }} size="small">
                            <InputLabel id="version-select-label">
                                Game version
                            </InputLabel>
                            <Select
                                labelId="version-select-label"
                                id="version-select"
                                value={versionConfig.version}
                                label="Game version"
                                onChange={handleGameVersionChange}
                            >
                                {Object.entries(versionConfigs)
                                    .sort(([a], [b]) => b.localeCompare(a))
                                    .map(([version, config]) => (
                                        <MenuItem value={version}>
                                            {version} - {config.title}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <span>
                            Build version: {versionConfig?.buildVersion}
                        </span>
                    </Box>
                </CardWithShadow>
            )}
        </Box>
    );
};

export default BattleGroundDetails;
